import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import AtmMachine from '../components/atm/AtmMachine';
import AtmShareModal from '../components/atm/AtmShareModal';
import AtmTransactionHistory from '../components/atm/AtmTransactionHistory';
import Layout from '../components/layout/Layout';
import { useAtm } from '../context/AtmContext';
import { useAuth } from '../hooks/useAuth';
import { Atm, AtmTransaction } from '../types/atm';
import { trackAtmTransaction } from '../utils/trackingUtils';

const AtmDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { fetchAtm, updateExistingAtm, error } = useAtm();
  const { isAuthenticated, currentUser: user } = useAuth();

  const [atm, setAtm] = useState<Atm | null>(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<AtmTransaction[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Load ATM data
  useEffect(() => {
    const loadAtm = async () => {
      console.log('📍 AtmDetail: Loading ATM with ID:', id);
      console.log('📍 AtmDetail: User authenticated?', isAuthenticated);
      console.log('📍 AtmDetail: Current user:', user?.uid || 'none');

      if (!id) {
        console.log('❌ AtmDetail: No ID provided, redirecting to explore');
        navigate('/explore-atms');
        return;
      }

      try {
        setLoading(true);
        console.log('📍 AtmDetail: Calling fetchAtm...');
        const atmData = await fetchAtm(id);
        console.log('📍 AtmDetail: fetchAtm returned:', atmData);

        if (!atmData) {
          console.log('📍 AtmDetail: No ATM data returned, redirecting to explore');
          navigate('/explore-atms');
          return;
        }

        console.log('📍 AtmDetail: Setting ATM data:', atmData);
        setAtm(atmData);

        // Load transactions from the ATM itself
        const atmTransactions = atmData.transactions || [];

        // Load transactions from all shared instances
        console.log('📍 AtmDetail: Loading shared ATM transactions...');
        const { getAtmShares, getSharedAtmTransactions } = await import(
          '../services/databaseIntegration'
        );

        try {
          const shares = await getAtmShares(id);
          console.log('📍 AtmDetail: Found', shares.length, 'shared instances');

          // Collect all transactions from shared instances
          const sharedTransactions: any[] = [];
          for (const share of shares) {
            const shareTransactions = await getSharedAtmTransactions(share.shareId);
            console.log(
              '📍 AtmDetail: Share',
              share.shareId,
              'has',
              shareTransactions.length,
              'transactions'
            );
            sharedTransactions.push(...shareTransactions);
          }

          // Combine and sort all transactions by timestamp
          const allTransactions = [...atmTransactions, ...sharedTransactions].sort((a, b) => {
            const timeA =
              a.timestamp instanceof Date ? a.timestamp.getTime() : a.timestamp.toMillis();
            const timeB =
              b.timestamp instanceof Date ? b.timestamp.getTime() : b.timestamp.toMillis();
            return timeB - timeA; // Most recent first
          });

          console.log('📍 AtmDetail: Total transactions:', allTransactions.length);
          setTransactions(allTransactions);
        } catch (shareError) {
          console.error('⚠️ AtmDetail: Error loading shared transactions:', shareError);
          // Fallback to just ATM transactions if shared loading fails
          setTransactions(atmTransactions);
        }
      } catch (err) {
        console.error('💥 AtmDetail: Error loading ATM:', err);
        navigate('/explore-atms');
      } finally {
        setLoading(false);
      }
    };

    loadAtm();
  }, [id, navigate]); // Remove fetchAtm dependency to prevent infinite loop

  // Handle transaction
  const handleTransaction = useCallback(
    async (transaction: AtmTransaction) => {
      if (!atm) return;

      try {
        // Update transaction with user info
        const updatedTransaction: AtmTransaction = {
          ...transaction,
          userId: user?.uid || 'anonymous',
        };

        // Update local state immediately
        const newTransactions = [...transactions, updatedTransaction];
        setTransactions(newTransactions);

        // Update ATM operations (decrease remaining count)
        const updatedAtm: Atm = {
          ...atm,
          options: atm.options.map(op =>
            op.type === transaction.type
              ? { ...op, enabled: true } // Keep enabled for now
              : op
          ),
          transactions: newTransactions,
          transactionCount: atm.transactionCount + 1,
          updatedAt: new Date(),
        };

        setAtm(updatedAtm);

        // Update in database
        await updateExistingAtm(atm.id, updatedAtm);

        // Track analytics
        trackAtmTransaction(atm.id, transaction.type, transaction.amount);
      } catch (err) {
        console.error('Error processing transaction:', err);
        // Revert optimistic update
        setTransactions(transactions);
        setAtm(atm);
      }
    },
    [atm, transactions, updateExistingAtm, user]
  );

  // Copy share link
  const copyShareLink = () => {
    const url = `${window.location.origin}/atm/${id}`;
    navigator.clipboard.writeText(url);
    // Could show toast notification
  };

  // Share functionality
  const handleShare = () => {
    if (!atm) return;
    // Open the share modal
    setShowShareModal(true);
  };

  // Close share modal
  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  // Check if user can edit
  const canEdit = atm && isAuthenticated && user && atm.createdBy === user.uid;

  // Check if ATM is accessible (always accessible if ATM exists - PIN check happens in ATM machine component)
  const isAccessible = !!atm;

  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">Loading ATM...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!atm || !isAccessible) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">ATM Not Found</h3>
            <p className="text-gray-600 mb-6">
              This ATM doesn&apos;t exist or you don&apos;t have permission to access it.
            </p>
            <Link
              to="/explore-atms"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Explore ATMs
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <Link to="/atms" className="text-blue-600 hover:text-blue-700 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to ATMs
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              {canEdit && (
                <Link
                  to={`/edit-atm/${atm.id}`}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Edit ATM
                </Link>
              )}
              <button
                onClick={copyShareLink}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                title="Copy simple link"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{atm.name}</h1>
            {atm.description && <p className="text-gray-600 mb-4">{atm.description}</p>}
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <div
                  className={`w-2 h-2 rounded-full mr-2 ${
                    atm.isPublic ? 'bg-green-500' : 'bg-gray-400'
                  }`}
                ></div>
                {atm.isPublic ? 'Public' : 'Private'}
              </div>
              <div>Created by {atm.createdByName || 'Anonymous User'}</div>
              <div>
                {atm.transactionCount} transaction{atm.transactionCount !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Main Content */}
        <div className="flex justify-center">
          {/* ATM Machine */}
          <AtmMachine
            atm={atm}
            onTransaction={handleTransaction}
            disabled={atm.options.every(op => !op.enabled)}
          />
        </div>

        {/* Share ATM Button */}
        <div className="flex justify-center mt-6">
          {!('isLocal' in atm) ? (
            // Active share button for cloud-stored ATMs
            <button
              onClick={handleShare}
              className="px-8 py-3 text-lg bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Share ATM
            </button>
          ) : (
            // Grayed-out share button for local ATMs
            <div className="relative group">
              <button
                disabled
                className="px-8 py-3 text-lg bg-gray-400 text-gray-200 rounded-lg cursor-not-allowed transition-colors"
                title="Sign in to share your ATM"
              >
                Share ATM
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                Sign in to share your ATM with others
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          )}
        </div>

        {/* Transaction History Toggle */}
        {transactions.length > 0 && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-6 py-3 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              {showHistory ? 'Hide' : 'View'} Transaction History ({transactions.length})
            </button>
          </div>
        )}

        {/* Transaction History */}
        {showHistory && transactions.length > 0 && (
          <div className="mt-8">
            <AtmTransactionHistory transactions={transactions} atmName={atm.name} maxItems={20} />
          </div>
        )}

        {/* Call to Action */}
        {!canEdit && (
          <div className="mt-8 text-center">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Create Your Own ATM</h3>
              <p className="text-gray-600 mb-4">
                Want to create your own interactive ATM? Get started now and customize your
                experience.
              </p>
              <Link
                to="/create-atm"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Create ATM
              </Link>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {atm && showShareModal && (
          <AtmShareModal atm={atm} isOpen={showShareModal} onClose={handleCloseShareModal} />
        )}
      </div>
    </Layout>
  );
};

export default AtmDetail;
